/**
 * The hours a reservation takes out of the calendar but does not charge for
 * (spec §10 · I1).
 *
 * > Rezerwacja 1 h wyjmuje 3 h z kalendarza `[start − 1h, koniec + 1h]`,
 * > bufor niepłatny.
 *
 * A studio hour is not an hour of a room's day. Somebody has to reset the
 * lights, clear the last podcast's coffee cups and be finished before the next
 * person walks in — and the calendar has to know, or it sells the room to two
 * people who then meet in the doorway.
 *
 * ── WHY THE BLOCKED WINDOW IS STORED, NOT DERIVED ────────────────────────
 * Every overlap test in the system compares two intervals. Deriving the buffer
 * at each of them means every caller has to remember to — the panel's
 * `assertFree`, the checkout's transaction, the calendar's paint — and the one
 * that forgets sells the room twice. `booking.blockedFrom`/`blockedTo` are
 * written once, by the same code that computes the price, and the overlap test
 * reads columns rather than re-deriving a rule.
 *
 * ── AND WHY IT IS BLOCKED-AGAINST-BLOCKED ────────────────────────────────
 * Two bookings clash when their BLOCKED windows meet, not when one's blocked
 * window meets the other's paid one. Spec §10 says a one-hour booking takes
 * three hours out of the calendar; if 10–11 and 12–13 could both be sold, the
 * two of them would take five hours between them rather than six, and the hour
 * from 11 to 12 would be doing teardown for the first and setup for the second
 * at the same time. So 10–11 blocks 09–12, and the next bookable hour is 13:00.
 *
 * Pure: minutes and instants in, minutes and instants out.
 */
import type { OfferingBookingMode } from '@repo/db/schema/offerings'

import type { DeliveryMethod } from './delivery.ts'

/** Spec §10's ±1 h around a studio booking. Unpaid, and never billed. */
export const STUDIO_BUFFER_MINUTES = 60

/**
 * Spec §11's unpaid days around a hire (I5).
 *
 * > Bufor odbiór osobisty: 1 dzień przed plus 1 dzień po. Niepłatny.
 * > Bufor kurier: 2 dni przed plus 2 dni po. Niepłatny.
 *
 * Twice as long for the courier because the parcel is out of our hands for
 * exactly that long: it has to travel there and travel back, and neither leg is
 * a day the next customer could have the camera. Personal collection needs one
 * day either side — the check-over, the card format, the missing lens cap.
 *
 * These are days the gear is genuinely UNAVAILABLE, which is why they land in
 * `booking.blocked_from`/`blocked_to` and why the database's EXCLUDE constraint
 * is written over that pair rather than over the paid window. Nobody is billed
 * for them.
 */
export const PICKUP_BUFFER_DAYS = 1
export const COURIER_BUFFER_DAYS = 2

/** Unpaid minutes either side of a hire, by how it travels. */
export function rentalBufferMinutes(delivery: DeliveryMethod): number {
  return (delivery === 'COURIER' ? COURIER_BUFFER_DAYS : PICKUP_BUFFER_DAYS) * 24 * 60
}

/** "Bądź 30 minut przed startem" — what the confirmation tells the customer. */
export const STUDIO_ARRIVE_EARLY_MINUTES = 30

/** Spec §10: "minimum 1 h". The floor a room may be booked for. */
export const STUDIO_MIN_MINUTES = 60

/** Studio hours run on the hour — 10–13, never 10:30–11:30. */
export const STUDIO_SLOT_MINUTES = 60

/**
 * How much unpaid room a booking of this kind needs on each side.
 *
 * Keyed on the booking MODE rather than on the offering, because it is a fact
 * about what is being held: a room needs an hour to be reset, a hire needs the
 * days it spends travelling and being checked over.
 *
 * `delivery` defaults to `PICKUP`, the shorter of the two: a hire whose method
 * nobody recorded is treated as a collection, which holds the shelf for one day
 * either side rather than two. The wrong direction to be wrong in is the other
 * one — a courier hire buffered as a collection would put the camera back on
 * the shelf while it is still in a parcel locker.
 *
 * Zero for everything with no calendar, which makes `blockedWindow` the
 * identity and leaves every existing reservation unchanged.
 */
export function bufferMinutesFor(
  mode: OfferingBookingMode,
  delivery: DeliveryMethod = 'PICKUP',
  /**
   * `offering.buffer_minutes` — this offering's own answer, when it has one.
   *
   * The mode is the right key for the DEFAULT and the wrong one for the only
   * `TIME_WINDOW` offering that is not a room. A strategy meeting buffered
   * like a studio takes three hours out of the day for one hour of talking,
   * which turned a twelve-hour office day into four bookable slots.
   *
   * `null`/`undefined` means "nothing to say" and falls through to the mode, so
   * every existing offering keeps the buffer it already had. `0` is a value.
   */
  overrideMinutes?: number | null,
): number {
  if (typeof overrideMinutes === 'number' && Number.isFinite(overrideMinutes)) {
    return Math.max(0, Math.trunc(overrideMinutes))
  }
  if (mode === 'TIME_WINDOW') return STUDIO_BUFFER_MINUTES
  if (mode === 'DATE_RANGE') return rentalBufferMinutes(delivery)
  return 0
}

/** The window a reservation actually takes out of the calendar. */
export function blockedWindow(
  startsAt: Date,
  endsAt: Date,
  bufferMinutes: number,
): { readonly blockedFrom: Date; readonly blockedTo: Date } {
  const pad = Math.max(0, bufferMinutes) * 60_000
  return {
    blockedFrom: new Date(startsAt.getTime() - pad),
    blockedTo: new Date(endsAt.getTime() + pad),
  }
}

/**
 * Why this clock window is not bookable — or `null` when it is.
 *
 * Spec §10 gives two rules and they refuse different things, so they get
 * different sentences: a half-hour start is a control the customer used wrongly
 * ("pełne godziny"), a too-short booking is a rule about the product ("minimum
 * 1 h"). One combined message would leave somebody who typed 10:30–12:30
 * reading about a minimum they already met.
 */
export function slotProblem(
  fromMinutes: number,
  toMinutes: number,
  minMinutes: number = STUDIO_MIN_MINUTES,
): string | null {
  if (toMinutes <= fromMinutes) return 'Godzina zakończenia musi być późniejsza niż rozpoczęcia.'
  if (fromMinutes % STUDIO_SLOT_MINUTES !== 0 || toMinutes % STUDIO_SLOT_MINUTES !== 0) {
    return 'Studio rezerwujemy na pełne godziny — np. 10:00–13:00.'
  }
  const minutes = toMinutes - fromMinutes
  if (minutes < minMinutes) {
    const hours = Math.max(1, Math.round(minMinutes / 60))
    return `Minimalna rezerwacja studia to ${hours} ${hours === 1 ? 'godzina' : 'godziny'}.`
  }
  return null
}

/**
 * Why this MOVED window is not bookable — or `null` when it is.
 *
 * A sibling of {@link slotProblem} and not the same function, because a move
 * is not a sale. The checkout asks "may we sell this window", and the answer
 * is the catalogue's: whole hours, at least the minimum. A move asks "may this
 * reservation sit here instead", and the reservation already exists at a
 * length somebody was charged for. Applying the sale rule to it would refuse
 * to move a booking that was legal when it was made — a 30-minute discovery
 * call is exactly that, sold as half an hour inside an hourly slot.
 *
 * So two rules, and they are the two a customer can actually break:
 *
 *   THE HOUR    A reservation starts on the hour. Every slot the site offers
 *               does, for a room and for a meeting alike, and 10:30 is a
 *               control used wrongly rather than a product decision.
 *   THE LENGTH  It may not come out SHORTER than the catalogue sells — unless
 *               it is simply keeping the length it already has, which nobody
 *               is being sold anything new for.
 *
 * Opening hours are the third rule and are deliberately not here: they need
 * the published calendar, and this file is pure.
 */
export function movedSlotProblem(
  current: { readonly startsAt: Date; readonly endsAt: Date },
  next: { readonly startsAt: Date; readonly endsAt: Date },
  startMinuteOfDay: number,
  minMinutes: number = STUDIO_MIN_MINUTES,
): string | null {
  if (startMinuteOfDay % STUDIO_SLOT_MINUTES !== 0) {
    return 'Termin zaczynamy o pełnej godzinie — np. 10:00.'
  }
  const was = (current.endsAt.getTime() - current.startsAt.getTime()) / 60_000
  const wants = (next.endsAt.getTime() - next.startsAt.getTime()) / 60_000
  if (wants <= 0) return 'Godzina zakończenia musi być późniejsza niż rozpoczęcia.'
  if (wants !== was && wants < minMinutes) {
    const hours = Math.max(1, Math.round(minMinutes / 60))
    return `Minimalna rezerwacja to ${hours} ${hours === 1 ? 'godzina' : 'godziny'}.`
  }
  return null
}

/** One opening the calendar publishes, as the availability table stores it. */
export interface OpenWindow {
  readonly startsAt: Date
  readonly endsAt: Date
  readonly isBookable: boolean
}

/**
 * Is the whole booking inside one published opening?
 *
 * Spec §10: "klient rezerwuje tylko z wpisanych godzin". Inside ONE window and
 * not merely covered by several, because two adjacent openings with a gap
 * between them are two separate offers of the room — a booking that spans the
 * gap would be sold time that was never published.
 *
 * A day with no windows at all is CLOSED, and that is the whole point of
 * turning the table on: an empty calendar has to mean "not open yet" rather
 * than "open always", or the feature would be a comment.
 */
export function isWithinOpenHours(
  startsAt: Date,
  endsAt: Date,
  windows: readonly OpenWindow[],
): boolean {
  return windows.some(
    (window) =>
      window.isBookable &&
      window.startsAt.getTime() <= startsAt.getTime() &&
      window.endsAt.getTime() >= endsAt.getTime(),
  )
}

/*
 * ── THERE USED TO BE A `canExtendTo` HERE ────────────────────────────────
 * Spec §10's „przedłużenie na miejscu": is the room free for another hour,
 * buffer and all, and still inside the published opening. It had its own unit
 * tests and read like a finished part of the system — and nothing called it.
 * No route, action, service or component: the only callers in the repository
 * were its own tests, so the feature it describes was never reachable.
 *
 * It is not a one-line reconnection either. `addSurcharge` takes an AMOUNT and
 * a kind (`STUDIO_EXTENSION`), never a new end time, so there is no hour to
 * check and no booking write to guard — extending on the spot means moving the
 * reservation's own `ends_at`, which is a feature to build rather than a call
 * to add. Deleted for the reason the other twenty-four dead exports were: code
 * that looks like a working rule, and is not, is worse than no code.
 *
 * The rule itself, for whoever builds it: the extra time is checked as
 * `blockedWindow(startsAt, newEndsAt, bufferMinutes)` against every other
 * booking's BLOCKED window with this reservation's own row excluded, and then
 * `isWithinOpenHours(startsAt, newEndsAt, windows)` on top — inside ONE
 * published opening, not merely covered by several.
 */
